// src/pages/ClientListPage.js
import React, { useEffect, useState } from "react";
import { Table, Button, Spinner, Alert, Form, InputGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import SalvationArmyBadge from "../components/SalvationArmyBadge";

function pickPhone(c) {
  return c?.mobile_phone || c?.home_phone || "";
}

function formatName(c) {
  if (c?.display_name) return c.display_name;
  const parts = [c?.first_name, c?.middle_name, c?.last_name].filter(Boolean);
  return parts.join(" ");
}

function formatEditor(c) {
  // Prefer the most recent editor; fall back to whoever created the record.
  const name = c?.updated_by_name || c?.created_by_name;
  if (!name) return "";
  const when = c?.updated_at || c?.created_at;
  const datePart = when ? formatDate(when) : "";
  return (
    <span>
      {name}
      {datePart && <span className="text-muted"> · {datePart}</span>}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "";
  const datePart = String(d).slice(0, 10);
  const [year, month, day] = datePart.split("-");
  if (year && month && day) return `${month}-${day}-${year}`;
  return datePart;
}

export default function ClientListPage() {
  const navigate = useNavigate();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    // Immediate load for empty search; 300 ms debounce while typing
    const delay = search.trim() ? 300 : 0;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const params = { per_page: 50 };
        if (search.trim()) params.q = search.trim();
        const res = await api.get("/clients", { params });
        setClients(res.data.items || []);
      } catch {
        setError("Failed to load clients");
      } finally {
        setLoading(false);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [search]);

  const openClient = (id) => navigate(`/clients/${id}/dashboard`);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Clients</h3>
        <Button as={Link} to="/clients/new" variant="primary">
          Add Client
        </Button>
      </div>

      <Form className="mb-3" onSubmit={(e) => e.preventDefault()}>
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search by name, phone, city, address…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => setSearch("")}
            >
              Clear
            </Button>
          )}
        </InputGroup>
      </Form>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted">
          <Spinner animation="border" size="sm" />
          <span>{search.trim() ? "Searching…" : "Loading…"}</span>
        </div>
      ) : (
        <Table striped bordered hover size="sm">
          <thead>
            <tr>
              <th>ID</th>
              <th>Intake Date</th>
              <th>Name</th>
              <th colSpan={5}>Phone</th>
              <th>City</th>
              <th>Birth Date</th>
              <th>Last Edited By</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr
                key={c.id}
                role="button"
                tabIndex={0}
                onClick={() => openClient(c.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openClient(c.id);
                  }
                }}
                style={{ cursor: "pointer" }}
                title="Open client dashboard"
              >
                <td>{c.id}</td>
                <td>{formatDate(c.intake_date)}</td>
                <td>
                  <span className="d-inline-flex align-items-center gap-2">
                    {formatName(c)}
                    <SalvationArmyBadge client={c} size={18} />
                  </span>
                </td>
                <td colSpan={5}>{pickPhone(c)}</td>
                <td>{c.city || ""}</td>
                <td>{formatDate(c.birth_date)}</td>
                <td>{formatEditor(c)}</td>
              </tr>
            ))}

            {clients.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center text-muted">
                  {search.trim()
                    ? `No clients found matching "${search.trim()}".`
                    : "No clients found."}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </>
  );
}
